import { englischeVorlage } from './englisch';
import { AUSGEMUSTERTE_IDS } from './standard';
import {
    AENDERBARE_FELDER,
    type AenderbaresFeld,
    type AufgeloesterKatalog,
    type AufgeloesterKnoten,
    type Dringlichkeit,
    type EnglischerKnoten,
    type KatalogKnoten,
    type KatalogOverlay,
    type KatalogSprache,
    type KnotenAenderung,
    type KnotenTyp,
    type MangelVorlage,
    type StandardKatalog,
} from './typen';
import { vorlageAusAuszug, vorlageAusKnoten } from './vorlage';

/**
 * Firmen-Overlay über dem Standard-Katalog (profiles.settings.mangelKatalog).
 *
 * `overlayNormalisieren` liest das JSON als `unknown` (es kommt aus der
 * Datenbank oder dem localStorage-Spiegel) und wirft nie; `katalogAufloesen`
 * baut daraus den Laufzeit-Katalog der Torte.
 */

const MAX_ID_LAENGE = 120;
const EIGEN_PRAEFIX = 'eigen_';
const KNOTEN_TYPEN: readonly KnotenTyp[] = ['kategorie', 'gruppe', 'mangel'];

type Roh = Record<string, unknown>;

const istObjekt = (v: unknown): v is Roh => !!v && typeof v === 'object' && !Array.isArray(v);

const idLesen = (v: unknown): string | null => {
    if (typeof v !== 'string') return null;
    const id = v.trim();
    return id && id.length <= MAX_ID_LAENGE ? id : null;
};

const textLesen = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);
const nichtLeer = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v : undefined);

const dringlichkeitLesen = (v: unknown): Dringlichkeit | undefined => (v === 'sofort' || v === 'frist' ? v : undefined);

/** Frist in Tagen: ganze Zahl 1–90, sonst weg. */
const fristTageLesen = (v: unknown): number | undefined => {
    if (typeof v !== 'number' || !Number.isFinite(v)) return undefined;
    const t = Math.round(v);
    return t >= 1 && t <= 90 ? t : undefined;
};

const versionLesen = (v: unknown): number | undefined =>
    typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : undefined;

/** Eindeutige IDs in Reihenfolge, Unbrauchbares fällt heraus. */
const idListeLesen = (v: unknown): string[] => {
    if (!Array.isArray(v)) return [];
    const aus: string[] = [];
    const gesehen = new Set<string>();
    for (const e of v) {
        const id = idLesen(e);
        if (id && !gesehen.has(id)) {
            gesehen.add(id);
            aus.push(id);
        }
    }
    return aus;
};

/** Nur die Felder der Positivliste, jedes mit passendem Typ. */
export function felderNormalisieren(roh: unknown): Partial<Pick<KatalogKnoten, AenderbaresFeld>> {
    const f: Partial<Pick<KatalogKnoten, AenderbaresFeld>> = {};
    if (!istObjekt(roh)) return f;
    for (const feld of AENDERBARE_FELDER) {
        const v = roh[feld];
        switch (feld) {
            case 'kurz':
            case 'name':
            case 'pikto':
            case 'kurzEn':
            case 'nameEn': {
                const t = nichtLeer(v);
                if (t !== undefined) f[feld] = t;
                break;
            }
            case 'titel':
            case 'text':
            case 'vorschrift':
            case 'titelEn':
            case 'textEn': {
                const t = textLesen(v);
                if (t !== undefined) f[feld] = t;
                break;
            }
            case 'dringlichkeit': {
                const d = dringlichkeitLesen(v);
                if (d) f.dringlichkeit = d;
                break;
            }
            case 'fristTage': {
                const t = fristTageLesen(v);
                if (t !== undefined) f.fristTage = t;
                break;
            }
        }
    }
    return f;
}

const aenderungLesen = (roh: unknown): KnotenAenderung | null => {
    if (!istObjekt(roh)) return null;
    const felder = felderNormalisieren(roh.felder);
    if (Object.keys(felder).length === 0) return null;
    return { rev: versionLesen(roh.rev) ?? 0, felder };
};

/**
 * Eigener Knoten der Firma; id muss 'eigen_…' sein (kein Zusammenstoß mit
 * Standard-IDs). Texte stehen inline — ein Verweis auf einen Eintrag des
 * Gesamtkatalogs (`quelle`, auch Altstände) wird nicht übernommen.
 */
const eigenenKnotenLesen = (roh: unknown): KatalogKnoten | null => {
    if (!istObjekt(roh)) return null;
    const id = idLesen(roh.id);
    if (!id || !id.startsWith(EIGEN_PRAEFIX)) return null;
    const typ = KNOTEN_TYPEN.find((t) => t === roh.typ);
    if (!typ) return null;
    const eltern = typ === 'kategorie' ? null : idLesen(roh.eltern);
    if (typ !== 'kategorie' && !eltern) return null;
    const kurz = nichtLeer(roh.kurz) ?? nichtLeer(roh.name) ?? nichtLeer(roh.titel);
    if (!kurz) return null;
    const k: KatalogKnoten = { id, typ, eltern, kurz };
    const name = nichtLeer(roh.name);
    if (name) k.name = name;
    const pikto = nichtLeer(roh.pikto);
    if (pikto) k.pikto = pikto;
    // Englische Fassung (28.09.), optional.
    const kurzEn = nichtLeer(roh.kurzEn);
    if (kurzEn) k.kurzEn = kurzEn;
    const nameEn = nichtLeer(roh.nameEn);
    if (nameEn) k.nameEn = nameEn;
    if (typ === 'mangel') {
        const titel = textLesen(roh.titel);
        if (titel !== undefined) k.titel = titel;
        const text = textLesen(roh.text);
        if (text !== undefined) k.text = text;
        const vorschrift = textLesen(roh.vorschrift);
        if (vorschrift !== undefined) k.vorschrift = vorschrift;
        const titelEn = textLesen(roh.titelEn);
        if (titelEn !== undefined) k.titelEn = titelEn;
        const textEn = textLesen(roh.textEn);
        if (textEn !== undefined) k.textEn = textEn;
    }
    const d = dringlichkeitLesen(roh.dringlichkeit);
    if (d) k.dringlichkeit = d;
    const frist = fristTageLesen(roh.fristTage);
    if (frist !== undefined) k.fristTage = frist;
    return k;
};

/**
 * Liest ein Overlay aus unbekannter Quelle. Nie werfen; unbekannte Felder
 * fallen weg, geänderte Felder nur nach AENDERBARE_FELDER. Kein Objekt oder
 * falsches Format → null.
 */
export function overlayNormalisieren(roh: unknown): KatalogOverlay | null {
    try {
        if (!istObjekt(roh) || roh.format !== 1) return null;
        const o: KatalogOverlay = {
            format: 1,
            ausgeblendet: idListeLesen(roh.ausgeblendet),
            geaendert: {},
            eigene: [],
            reihenfolge: {},
        };
        const gesehen = versionLesen(roh.gesehenBisVersion);
        if (gesehen !== undefined) o.gesehenBisVersion = gesehen;
        if (roh.nachHaeufigkeit === false) o.nachHaeufigkeit = false;
        if (istObjekt(roh.geaendert)) {
            for (const [schluessel, wert] of Object.entries(roh.geaendert)) {
                const id = idLesen(schluessel);
                const a = aenderungLesen(wert);
                if (id && a) o.geaendert[id] = a;
            }
        }
        if (Array.isArray(roh.eigene)) {
            const ids = new Set<string>();
            for (const e of roh.eigene) {
                const k = eigenenKnotenLesen(e);
                if (k && !ids.has(k.id)) {
                    ids.add(k.id);
                    o.eigene.push(k);
                }
            }
        }
        if (istObjekt(roh.reihenfolge)) {
            for (const [schluessel, wert] of Object.entries(roh.reihenfolge)) {
                // '' = Wurzel ist ein gültiger Schlüssel.
                const eltern = schluessel === '' ? '' : idLesen(schluessel);
                if (eltern === null) continue;
                const liste = idListeLesen(wert);
                if (liste.length) o.reihenfolge[eltern] = liste;
            }
        }
        return o;
    } catch {
        return null;
    }
}

export function leeresOverlay(): KatalogOverlay {
    return { format: 1, ausgeblendet: [], geaendert: {}, eigene: [], reihenfolge: {} };
}

/** Anzeigename: name, sonst kurz ohne Zeilenumbruch ('Seiten-\nschutz' → 'Seitenschutz'). */
export function knotenName(k: Pick<KatalogKnoten, 'kurz' | 'name'>): string {
    const name = nichtLeer(k.name);
    if (name) return name.trim();
    return (k.kurz ?? '').replace(/-\n/g, '').replace(/\s*\n\s*/g, ' ').trim();
}

/**
 * Beschriftung in der Katalogsprache. Englisch (28.09.): kurzEn/nameEn des
 * Overlays bzw. eigenen Knotens → englischer Standard → deutsch. Ein
 * englischer Kurztext ohne englischen Namen liefert den Namen selbst
 * (wie deutsch).
 */
function beschriftungIn(
    k: KatalogKnoten,
    sprache: KatalogSprache,
    englisch: EnglischerKnoten | undefined,
): { kurz: string; name: string } {
    const deutsch = { kurz: k.kurz ?? '', name: knotenName(k) };
    if (sprache !== 'en') return deutsch;
    const kurz = nichtLeer(k.kurzEn) ?? nichtLeer(englisch?.kurz);
    const name = nichtLeer(k.nameEn)?.trim() ?? nichtLeer(englisch?.name)?.trim();
    if (kurz === undefined && name === undefined) return deutsch;
    return { kurz: kurz ?? deutsch.kurz, name: name ?? (kurz !== undefined ? knotenName({ kurz }) : deutsch.name) };
}

interface Eintrag {
    knoten: KatalogKnoten;
    herkunft: 'standard' | 'eigen';
    geaendert: boolean;
    /** Position in Standard + Eigenen (Rückfall-Reihenfolge). */
    pos: number;
}

/**
 * Baut den Laufzeit-Katalog: Standard + Overlay.
 * - Ausblenden vererbt sich auf Kinder; eigene Knoten mit unbekanntem
 *   Elternteil fallen heraus (nur von der Wurzel erreichbare Knoten zählen).
 * - Reihenfolge: overlay.reihenfolge[eltern] zuerst (unbekannte IDs
 *   ignoriert), fehlende Kinder hinten in Standardreihenfolge.
 * - Mangel ohne auflösbare Vorlage fällt heraus.
 * - pikto erbt; Rückfall 'allgemein'.
 * - neu: nur mit Overlay und nur für Standardknoten, deren `seit` über
 *   overlay.gesehenBisVersion (sonst der Version des Standards) liegt.
 * - IDs in AUSGEMUSTERTE_IDS erscheinen nie.
 * - sprache 'en' (28.09.): Kurztext, Name und Vorlage englisch — Overlay-
 *   bzw. eigene Felder …En vor der Standardübersetzung (standard.englisch),
 *   sonst deutsch; die Vorschrift in englischer Kurzform. Struktur, IDs,
 *   Piktogramme und Reihenfolge sind in beiden Sprachen gleich.
 */
export function katalogAufloesen(
    standard: StandardKatalog,
    overlay: KatalogOverlay | null,
    sprache: KatalogSprache = 'de',
): AufgeloesterKatalog {
    const o = overlay ? overlayNormalisieren(overlay) : null;
    const ausgemustert = new Set(AUSGEMUSTERTE_IDS);
    const auszug = standard?.auszug ?? {};
    const version = standard?.version ?? 0;
    const englisch = sprache === 'en' ? standard?.englisch : undefined;

    const eintraege = new Map<string, Eintrag>();
    let pos = 0;
    for (const std of standard?.knoten ?? []) {
        if (!std || typeof std.id !== 'string' || !std.id || ausgemustert.has(std.id) || eintraege.has(std.id)) continue;
        const aenderung = o?.geaendert[std.id];
        const felder = aenderung?.felder ?? {};
        const geaendert = Object.keys(felder).length > 0;
        eintraege.set(std.id, { knoten: geaendert ? { ...std, ...felder } : std, herkunft: 'standard', geaendert, pos: pos++ });
    }
    for (const eigen of o?.eigene ?? []) {
        if (ausgemustert.has(eigen.id) || eintraege.has(eigen.id)) continue;
        eintraege.set(eigen.id, { knoten: eigen, herkunft: 'eigen', geaendert: false, pos: pos++ });
    }

    // Kinder je Elternteil in Rückfall-Reihenfolge (Standard, dann Eigene).
    const kinderRoh = new Map<string, string[]>();
    for (const [id, e] of eintraege) {
        const k = e.knoten;
        let schluessel: string;
        if (k.typ === 'kategorie') {
            if (k.eltern !== null && k.eltern !== undefined) continue;
            schluessel = '';
        } else {
            if (typeof k.eltern !== 'string' || !k.eltern) continue;
            schluessel = k.eltern;
        }
        const liste = kinderRoh.get(schluessel);
        if (liste) liste.push(id);
        else kinderRoh.set(schluessel, [id]);
    }

    const geordnet = (eltern: string): string[] => {
        const vorhanden = kinderRoh.get(eltern) ?? [];
        const wunsch = o?.reihenfolge[eltern];
        if (!wunsch?.length) return vorhanden;
        const menge = new Set(vorhanden);
        const vorne = wunsch.filter((id) => menge.has(id));
        const vorneMenge = new Set(vorne);
        return [...vorne, ...vorhanden.filter((id) => !vorneMenge.has(id))];
    };

    const verborgen = new Set(o?.ausgeblendet ?? []);
    const gesehenBis = o?.gesehenBisVersion ?? version;
    const knoten = new Map<string, AufgeloesterKnoten>();
    const kinder = new Map<string, readonly string[]>();
    const ausgeblendet = new Set<string>();

    /** Versteckt einen Teilbaum (nur für den Editor gemerkt). */
    const verstecken = (id: string, besucht: Set<string>) => {
        if (besucht.has(id)) return;
        besucht.add(id);
        ausgeblendet.add(id);
        const e = eintraege.get(id);
        if (!e || e.knoten.typ === 'mangel') return;
        for (const kind of kinderRoh.get(id) ?? []) verstecken(kind, besucht);
    };

    const besucht = new Set<string>();
    const besuchen = (id: string, eltern: AufgeloesterKnoten | null, sichtbareGeschwister: string[]) => {
        if (besucht.has(id)) return;
        const e = eintraege.get(id);
        if (!e) return;
        if (verborgen.has(id)) {
            verstecken(id, besucht);
            return;
        }
        besucht.add(id);
        const k = e.knoten;
        // Ebenenregel: Kategorie nur in der Wurzel, unter einem Mangel hängt nichts.
        if ((eltern === null) !== (k.typ === 'kategorie')) return;
        // Standardübersetzung nur für Standardknoten (eigene tragen ihr Englisch inline).
        const englischerKnoten = englisch && e.herkunft === 'standard' ? englisch.knoten[id] : undefined;
        const { kurz, name } = beschriftungIn(k, sprache, englischerKnoten);
        let vorlage: MangelVorlage | undefined;
        if (k.typ === 'mangel') {
            // Nur Standardknoten verweisen auf einen Eintrag (quelle[0]); eigene tragen ihre Texte inline.
            const quelleId = e.herkunft === 'standard' && Array.isArray(k.quelle) ? k.quelle[0] : undefined;
            const erster = quelleId ? auszug[quelleId] : undefined;
            vorlage = (erster ? vorlageAusAuszug(erster, k) : vorlageAusKnoten(k)) ?? undefined;
            if (!vorlage) return;
            if (sprache === 'en') {
                vorlage = englischeVorlage(vorlage, k, {
                    knoten: englischerKnoten,
                    auszug: erster && quelleId && englisch ? englisch.auszug[quelleId] : undefined,
                });
            }
        }
        const aufgeloest: AufgeloesterKnoten = {
            id,
            typ: k.typ,
            eltern: eltern ? eltern.id : null,
            kurz,
            name,
            pikto: nichtLeer(k.pikto)?.trim() ?? eltern?.pikto ?? 'allgemein',
            kategorieId: eltern ? eltern.kategorieId : id,
            pfad: eltern ? [...eltern.pfad, name] : [name],
            herkunft: e.herkunft,
            geaendert: e.geaendert,
            neu: e.herkunft === 'standard' && o !== null && (k.seit ?? 0) > gesehenBis,
        };
        if (vorlage) aufgeloest.vorlage = vorlage;
        knoten.set(id, aufgeloest);
        sichtbareGeschwister.push(id);
        if (k.typ === 'mangel') return;
        const eigeneKinder: string[] = [];
        for (const kind of geordnet(id)) besuchen(kind, aufgeloest, eigeneKinder);
        kinder.set(id, eigeneKinder);
    };

    const wurzel: string[] = [];
    for (const id of geordnet('')) besuchen(id, null, wurzel);
    kinder.set('', wurzel);

    return { version, knoten, kinder, ausgeblendet };
}
